import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { rowRemovingConfirmation } from '@asamco/application-link/shared';
import { AsamcoAuthService } from '@asamco/base';
import { showErrorSwal } from '@asamco/utils';
import { Group, GroupService, UserInGroupDto } from '@asamco/user-management/shared';
import { Subject } from '@microsoft/signalr';
import { DxButtonModule, DxCheckBoxModule, DxDataGridModule, DxTextBoxModule } from 'devextreme-angular';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxiColumnModule, DxoEditingModule, DxoFilterRowModule, DxoPagingModule, DxoSearchPanelModule, DxoSortingModule } from 'devextreme-angular/ui/nested';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { RowRemovingEvent } from 'devextreme/ui/data_grid';
import { Observable, catchError, firstValueFrom, map, of, throwError, timeout } from 'rxjs';

const hiddenLegacyGroupNames = new Set([
	'Super Administrators',
	'Administrators',
	'Users',
]);

const superApplicationLinkAdministratorsGroupName = 'Super ApplicationLink Administrators';

type ViewModel = {
	state: 'loading'
} | {
	state: 'ready'
	dataSource: DataSource;
} | {
	state: 'error'
}

@Component({
	selector: 'app-shell-admin-groups-management-grid',
	templateUrl: './admin-groups-management-grid.component.html',
	styleUrls: ['./admin-groups-management-grid.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		AsyncPipe,
		DxButtonModule,
		DxCheckBoxModule,
		DxDataGridModule,
		DxiColumnModule,
		DxoEditingModule,
		DxoFilterRowModule,
		DxoPagingModule,
		DxoSearchPanelModule,
		DxoSortingModule,
		DxTemplateModule,
		DxTextBoxModule,
		NgIf,
	],
})
export class AdminGroupsManagementGridComponent implements OnDestroy {
	private readonly destroy$ = new Subject<void>();
	public canCreateGroups = false;
	public canDeleteGroups = false;

	public readonly viewModel$: Observable<ViewModel> = of({
		state: 'loading',
	}).pipe(
		map(() => {
			const viewModel: ViewModel = {
				state: 'ready',
				dataSource: this.buildDataSource(),
			};
			return viewModel;
		}),
		catchError(error => {
			showErrorSwal(error);
			const viewModel: ViewModel = {
				state: 'error',
			};
			return of(viewModel);
		}),
	);

	constructor(
		private activatedRoute: ActivatedRoute,
		private asamcoAuthService: AsamcoAuthService,
		private changeDetectorRef: ChangeDetectorRef,
		private groupService: GroupService,
		private router: Router,
	) {
		void this.initializePermissions();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private async initializePermissions(): Promise<void> {
		const [groups, usersPerGroup] = await Promise.all([
			firstValueFrom(this.groupService.getAll().pipe(timeout(15000))),
			firstValueFrom(this.groupService.getUsersPerGroup('', 0).pipe(timeout(15000))),
		]);
		const canManageGroups = this.isCurrentUserSuperApplicationLinkAdmin(groups, usersPerGroup);
		this.canCreateGroups = canManageGroups;
		this.canDeleteGroups = canManageGroups;
		this.changeDetectorRef.markForCheck();
	}

	public rowRemoving($event: RowRemovingEvent<Group>): void {
		$event.cancel = rowRemovingConfirmation({
			text: `Delete group '${$event.data.name}'?`,
		});
	}

	public readonly openGroupFromCell = (cell: { data?: Group & { ID?: number } }) => {
		const groupId = cell.data?.id ?? cell.data?.ID;
		if (!groupId) {
			return;
		}

		void this.router.navigate(['./', groupId], { relativeTo: this.activatedRoute });
	};

	private buildDataSource(): DataSource {
		return new DataSource<Group, Group>({
			key: 'id',
			store: new CustomStore({
				load: () => {
					return firstValueFrom(this.groupService.getAll().pipe(
						map(groups => groups.filter(group => !hiddenLegacyGroupNames.has(group.name ?? ''))),
						this.handleDataStoreError(),
					));
				},
				byKey: (key: Group) => {
					return firstValueFrom(this.groupService.get(key.id).pipe(this.handleDataStoreError()));
				},
				insert: values => {
					return firstValueFrom(this.groupService.save(values).pipe(this.handleDataStoreError()));
				},
				update: (key: Group, values: Group) => {
					const newValues = Object.assign({}, key, values);
					return firstValueFrom(this.groupService.save(newValues).pipe(this.handleDataStoreError()));
				},
				remove: async (key: Group) => {
					await firstValueFrom(this.groupService.delete(key.id).pipe(this.handleDataStoreError(), map(() => undefined)));
				},
			}),
		});
	}

	private handleDataStoreError<T>(): (source: Observable<T>) => Observable<T> {
		return (source: Observable<T>): Observable<T> =>
			source.pipe(
				catchError(error => {
					showErrorSwal(error);
					return throwError(() => error);
				}),
			);
	}

	private isCurrentUserSuperApplicationLinkAdmin(groups: Group[], usersPerGroup: UserInGroupDto[]): boolean {
		const currentUserId = this.asamcoAuthService.userSession?.user?.userId;
		if (!currentUserId) {
			return false;
		}

		return usersPerGroup
			.filter(userInGroup => userInGroup.userId === currentUserId)
			.some(userInGroup => {
				const groupName = groups.find(group => group.id === userInGroup.groupId)?.name ?? userInGroup.groupName;
				return groupName?.toLocaleLowerCase() === superApplicationLinkAdministratorsGroupName.toLocaleLowerCase();
			});
	}
}
